/**
 * Shared money math for billed-time entries.
 *
 * The stored `billed_time.amount` is the authoritative value for an entry —
 * it is computed server-side at log time and may have been manually adjusted
 * (write-down, fee cap, negotiated discount). `rate × hours` is only a
 * fallback for legacy rows that never had an amount saved.
 */

/** Parse a time_recorded value ("HH:MM" / "HH:MM:SS" or decimal hours) into decimal hours. */
export function parseTimeToHours(timeRecorded: unknown): number {
  const s = String(timeRecorded ?? '').trim();
  if (!s) return 0;
  if (s.includes(':')) {
    // HH:MM:SS — include seconds to match the backend (EPOCH/3600); dropping them
    // caused penny drift between the wizard display and backend uninvoiced totals (H11).
    const parts = s.split(':');
    const h = parseInt(parts[0] || '0', 10) || 0;
    const m = parseInt(parts[1] || '0', 10) || 0;
    const sec = parseInt(parts[2] || '0', 10) || 0;
    return h + m / 60 + sec / 3600;
  }
  return parseFloat(s) || 0;
}

/**
 * The total dollar value of a billed-time entry.
 * Prefers the stored amount; falls back to rate × hours when no amount exists.
 */
export function computeBilledTimeAmount(bt: {
  amount?: string | number | null;
  time_recorded?: string | null;
  billedrate?: string | number | null;
}): number {
  const stored = parseFloat(String(bt.amount ?? ''));
  // Any finite stored amount is authoritative — including an intentional 0
  // (write-down / pro-bono). Only a missing/unparseable amount (NaN) falls back.
  if (Number.isFinite(stored)) return stored;
  const rate = parseFloat(String(bt.billedrate ?? '')) || 0;
  return parseTimeToHours(bt.time_recorded) * rate;
}

/**
 * The per-hour rate that, multiplied by the entry's hours, reproduces the
 * true billed total exactly (qty × rate = total). For adjusted entries this
 * is the effective rate, not the nominal billedrate.
 */
export function effectiveHourlyRate(bt: {
  amount?: string | number | null;
  time_recorded?: string | null;
  billedrate?: string | number | null;
}): number {
  const total = computeBilledTimeAmount(bt);
  const hours = parseTimeToHours(bt.time_recorded);
  return hours > 0 ? total / hours : total;
}
